import { useResource } from '@landsbankinn/l16n';
import { Button, useMedia, theme } from '@landsbankinn/ui';
import React from 'react';
import type { fetchFunds_funds_nodes } from '../../../../graphql/__generated__/fetchFunds';
import {
  MarketsBodyBold,
  MarketsNumberSmall,
  MarketsSmall,
} from '../../shared/MarketsComponents';
import {
  MarketsFormatNumber,
  NumberType,
} from '../../shared/MarketsFormatNumber';
import { AnnualizedReturnOptions } from '../returns-selector-tabs/useReturnsConfig';
import { RiskRating, RiskRatingSize } from '../risk-rating/RiskRating';
import res from './../Funds.res';
import { ButtonContainer } from './useNominalData';

export function useAnnualizedData(
  data: fetchFunds_funds_nodes[],
  category: string,
  selectedReturn: AnnualizedReturnOptions,
  fundBuyUrl: (symbol: string) => string,
  stockBuyUrl: (symbol: string) => string,
) {
  const { r } = useResource();
  const isMobile = useMedia('sm');

  return React.useMemo(() => {
    if (isMobile) {
      const mobileArray = [
        {
          Header: category,
          accessor: 'shortName',
          headerStyle: {
            flexDirection: 'row-reverse',
          },
          Cell: ({ value, row: { original: fund } }: any) => (
            <MarketsBodyBold>
              {value}
              {fund.details?.ucitsClassification
                .toLocaleLowerCase()
                .includes('non-ucits') && <>*</>}
            </MarketsBodyBold>
          ),
        },

        {
          accessor: 'currency',
          style: {
            textAlign: 'right',
          },
          Cell: ({ value }: any) => <MarketsSmall>{value}</MarketsSmall>,
        },
        {
          accessor: 'details.riskRating',
          Cell: ({ value }: any) => (
            <RiskRating
              value={value}
              size={isMobile ? RiskRatingSize.Small : RiskRatingSize.Normal}
            />
          ),
          style: {
            textAlign: 'right',
          },
        },
        selectedReturn === AnnualizedReturnOptions.OneYear
          ? {
              accessor: 'annualizedReturns.oneYearReturn',
              sortType: 'basic',
              Cell: ({ value }: any) =>
                value !== '' && (
                  <MarketsNumberSmall
                    type={NumberType.Percentage}
                    decimalScale={2}
                    hideZero
                    hideNullIndicator
                    displayPosNegColor
                    number={value}
                  />
                ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === AnnualizedReturnOptions.TwoYears
          ? {
              accessor: 'annualizedReturns.twoYearReturn',
              sortType: 'basic',
              Cell: ({ value }: any) =>
                value !== '' && (
                  <MarketsNumberSmall
                    type={NumberType.Percentage}
                    decimalScale={2}
                    hideZero
                    hideNullIndicator
                    displayPosNegColor
                    number={value}
                  />
                ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === AnnualizedReturnOptions.ThreeYears
          ? {
              accessor: 'annualizedReturns.threeYearReturn',
              sortType: 'basic',
              Cell: ({ value }: any) =>
                value !== '' && (
                  <MarketsNumberSmall
                    type={NumberType.Percentage}
                    decimalScale={2}
                    hideZero
                    hideNullIndicator
                    displayPosNegColor
                    number={value}
                  />
                ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === AnnualizedReturnOptions.FourYears
          ? {
              accessor: 'annualizedReturns.fourYearReturn',
              sortType: 'basic',
              Cell: ({ value }: any) =>
                value !== '' && (
                  <MarketsNumberSmall
                    type={NumberType.Percentage}
                    decimalScale={2}
                    hideZero
                    hideNullIndicator
                    displayPosNegColor
                    number={value}
                  />
                ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
        selectedReturn === AnnualizedReturnOptions.FiveYears
          ? {
              accessor: 'annualizedReturns.fiveYearReturn',
              sortType: 'basic',
              Cell: ({ value }: any) =>
                value !== '' && (
                  <MarketsNumberSmall
                    type={NumberType.Percentage}
                    decimalScale={2}
                    hideZero
                    hideNullIndicator
                    displayPosNegColor
                    number={value}
                  />
                ),
              style: {
                textAlign: 'right',
              },
            }
          : null,
      ];

      return mobileArray.filter(function (i) {
        return i != null;
      });
    } else {
      return [
        {
          Header: category,
          accessor: 'shortName',
          headerStyle: {
            flexDirection: 'row-reverse',
          },
          Cell: ({ value, row: { original: fund } }: any) => (
            <MarketsBodyBold>
              {value}
              {fund.details?.ucitsClassification
                .toLocaleLowerCase()
                .includes('non-ucits') && <>*</>}
            </MarketsBodyBold>
          ),
        },
        {
          Header: r(res.currency),
          accessor: 'currency',
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.oneYear),
          sortType: 'basic',
          accessor: (fund: any) => fund.annualizedReturns.oneYearReturn ?? '',
          Cell: ({ value }: any) =>
            value !== '' && (
              <MarketsFormatNumber
                type={NumberType.Percentage}
                decimalScale={2}
                hideZero
                hideNullIndicator
                displayPosNegColor
                number={value}
              />
            ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.twoYears),
          sortType: 'basic',
          accessor: (fund: any) => fund.annualizedReturns.twoYearReturn ?? '',
          Cell: ({ value }: any) =>
            value !== '' && (
              <MarketsFormatNumber
                type={NumberType.Percentage}
                decimalScale={2}
                hideZero
                hideNullIndicator
                displayPosNegColor
                number={value}
              />
            ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.threeYears),
          sortType: 'basic',
          accessor: (fund: any) => fund.annualizedReturns.threeYearReturn ?? '',
          Cell: ({ value }: any) =>
            value !== '' && (
              <MarketsFormatNumber
                type={NumberType.Percentage}
                decimalScale={2}
                hideZero
                hideNullIndicator
                displayPosNegColor
                number={value}
              />
            ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.fourYears),
          sortType: 'basic',
          accessor: (fund: any) => fund.annualizedReturns.fourYearReturn ?? '',
          Cell: ({ value }: any) =>
            value !== '' && (
              <MarketsFormatNumber
                type={NumberType.Percentage}
                decimalScale={2}
                hideZero
                hideNullIndicator
                displayPosNegColor
                number={value}
              />
            ),
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: r(res.fiveYears),
          sortType: 'basic',
          accessor: (fund: any) => fund.annualizedReturns.fiveYearReturn ?? '',
          Cell: ({ value }: any) =>
            value !== '' && (
              <MarketsFormatNumber
                type={NumberType.Percentage}
                decimalScale={2}
                hideZero
                hideNullIndicator
                displayPosNegColor
                number={value}
              />
            ),
          style: {
            textAlign: 'right',
          },
        },

        {
          Header: r(res.volatility),
          accessor: 'details.riskRating',
          Cell: ({ value }: any) => <RiskRating value={value} />,
          style: {
            textAlign: 'right',
          },
        },
        {
          Header: '',
          accessor: 'details.onlineBuyAllowed',
          Cell: ({ value, row: { original: fund } }: any) =>
            value ? (
              <ButtonContainer>
                <Button
                  as="a"
                  title={r(res.buy)}
                  isSmall
                  href={
                    fund.symbol !== 'LEQ'
                      ? fundBuyUrl(fund.symbol)
                      : stockBuyUrl(fund.symbol)
                  }
                  bg={theme.colors.primary}
                  onClick={(e) => e.stopPropagation()}
                />
              </ButtonContainer>
            ) : (
              <ButtonContainer></ButtonContainer>
            ),
          style: {
            textAlign: 'right',
          },
        },
      ];
    }
  }, [data, selectedReturn]);
}
